import * as React from 'react';
import cx from 'classnames';
import { Flex } from '@common/components/Flex';
import { Hint } from '@common/components/hint';
import { ErrorText } from '@common/components/error-text';

import './field.styles.scss';

export type FieldError = {
  text: string;
};

export enum FieldLayout {
  labelThenComponent = 'label-then-component',
  componentThenLabel = 'component-the-label'
}

export type FieldProps = React.PropsWithChildren<{
  layout?: FieldLayout;
  label?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  labelWidth?: number | string;
  description?: React.ReactNode;
  required?: boolean;
  margin?: string | number;
  classes?: {
    label?: string;
    input?: string;
    labelInputWrapper?: string;
    errorText?: string;
  };
  inputWidth?: number | string;
  errors?: Array<FieldError>;
  onLabelClick?: () => void;
}>;

export const Field: React.FC<FieldProps> = (props) => {
  const { onLabelClick, layout, classes, className, disabled, label, children, labelWidth, required, description, margin = '12px 0 0 0', errors, inputWidth } = props;

  const computedLabelWidth = React.useMemo(() => {
    if (layout === FieldLayout.componentThenLabel) {
      return labelWidth;
    }

    return labelWidth ?? 250;
  }, [labelWidth, layout]);

  const computedInputWidth = inputWidth ?? 350;

  return (
    <div
      className={cx(
        'FhqDesignToolField', 
        className
      )}
      style={{ margin }}
    >
      <Flex 
        className={cx("FhqDesignToolField__label-input-wrapper", classes?.labelInputWrapper)}
        alignItems="center"
        justify={layout === FieldLayout.componentThenLabel ? 'flex-end' : undefined}
        direction={layout === FieldLayout.componentThenLabel ? 'row-reverse' : undefined}
      >
        {label && (
          <span 
            className={cx(
              "FhqDesignToolField__label", 
              layout === FieldLayout.componentThenLabel 
                ? 'FhqDesignToolField__label--component-label' 
                : 'FhqDesignToolField__label--label-component',
              classes?.label
            )} 
            style={{ width: computedLabelWidth }}
            onClick={disabled ? undefined : onLabelClick}
          >
            {label}{" "}
            {required && <span className="FhqDesignToolField__required-mark">*</span>}
            {description && <Hint className="FhqDesignToolField__description-mark" description={description} />}
          </span>
        )}
        <div 
          className={cx("FhqDesignToolField__input", { 'FhqDesignToolField__input--disabled': disabled }, classes?.input)}
          style={{ width: computedInputWidth }}
        >
          {children}
        </div>
      </Flex>

      {errors?.map((error, i) => (
        <ErrorText
          withTopGap
          style={{ maxWidth: computedInputWidth }}
          text={error.text} 
          key={typeof error.text === 'string' ? error.text : i}
          className={classes?.errorText} 
        />
      ))}
    </div>
  );
};